/* ============================================================
 * frontend-v3/src/components/FolderBreadcrumbs.tsx
 *
 * NMW-931 (parent NMW-913) — the root → folder → subfolder trail with
 * drill-out clicks, shared by the Library and the Channels screens.
 *
 * Why not the existing `primitives/Breadcrumbs`: that one renders `href`
 * anchors (a route change) and always styles the last crumb as inert. A
 * folder trail is in-screen STATE, not a route — drilling out must call
 * back into the screen without a navigation, and the current folder must
 * stay visible as the active crumb. `Breadcrumbs` is also sized for the
 * page head (14px, 6px gaps); this one is deliberately denser. It is left
 * untouched for the screens that do route with it.
 *
 * COMPACT by owner constraint: one 24px-tall line, 12px type, icon-only
 * root, no chrome. It is a header strip inside a toolbar, not a hero.
 *
 * Honest-state: when `folderId` is set but does not resolve in `folders`
 * (a stale deep-link, a folder deleted in another tab) the component says
 * so with an explicit "Carpeta desconocida" crumb instead of silently
 * rendering the root, which would read as "you are at the top" and hide a
 * real inconsistency.
 * ============================================================ */
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from './primitives';
import { folderTrail, type FolderNode } from '../lib/folderTree';

export interface FolderBreadcrumbsProps {
  /** Every folder of the domain (flat) — typically `api.folders.listFolders`. */
  folders: readonly FolderNode[];
  /** The folder currently being browsed; null / undefined ⇒ the domain root. */
  folderId?: string | null;
  /** Label for the root crumb, e.g. "Biblioteca" / "Canales". */
  rootLabel: string;
  /** Drill-out: `null` means "go back to the domain root". */
  onNavigate: (folderId: string | null) => void;
  /** Optional right-hand slot (item count, view switch…). */
  right?: ReactNode;
}

const CRUMB_BTN: CSSProperties = {
  border: 'none',
  background: 'none',
  padding: '1px 4px',
  margin: 0,
  font: 'inherit',
  fontSize: 12,
  lineHeight: '18px',
  borderRadius: 4,
  cursor: 'pointer',
  color: 'var(--muted-slate)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  maxWidth: 200,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export function FolderBreadcrumbs({
  folders,
  folderId,
  rootLabel,
  onNavigate,
  right,
}: FolderBreadcrumbsProps) {
  const trail = folderTrail(folders, folderId);
  const unresolved = Boolean(folderId) && trail.length === 0;
  const atRoot = !folderId;

  return (
    <nav
      aria-label="Ruta de carpetas"
      data-testid="folder-breadcrumbs"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        minHeight: 24,
        fontSize: 12,
        color: 'var(--muted-slate)',
      }}
    >
      <button
        type="button"
        data-testid="folder-crumb-root"
        aria-current={atRoot ? 'page' : undefined}
        disabled={atRoot}
        onClick={() => onNavigate(null)}
        title={rootLabel}
        style={{
          ...CRUMB_BTN,
          color: atRoot ? 'var(--ink-muted)' : 'var(--muted-slate)',
          cursor: atRoot ? 'default' : 'pointer',
        }}
      >
        <Icon name="home" size={12} />
        <span>{rootLabel}</span>
      </button>

      {trail.map((node, i) => {
        const isLast = i === trail.length - 1;
        return (
          <span key={node._id} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
            <Icon name="chevronR" size={11} color="var(--muted-slate)" />
            <button
              type="button"
              data-testid="folder-crumb"
              data-folder-id={node._id}
              aria-current={isLast ? 'page' : undefined}
              disabled={isLast}
              onClick={() => onNavigate(node._id)}
              title={node.name}
              style={{
                ...CRUMB_BTN,
                color: isLast ? 'var(--ink-muted)' : 'var(--muted-slate)',
                fontWeight: isLast ? 500 : 400,
                cursor: isLast ? 'default' : 'pointer',
              }}
            >
              {node.name}
            </button>
          </span>
        );
      })}

      {unresolved && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
          <Icon name="chevronR" size={11} color="var(--muted-slate)" />
          <span
            data-testid="folder-crumb-unknown"
            style={{ ...CRUMB_BTN, color: 'var(--bad)', cursor: 'default' }}
          >
            Carpeta desconocida
          </span>
        </span>
      )}

      {right && <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>{right}</span>}
    </nav>
  );
}

export default FolderBreadcrumbs;
